import { AuthContext } from "hooks/authProvider/authProvider";
import { type FC, useContext } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import AdminDashboardPageContainer from "routes/admin/AdminDashboardPage/index";
import { ContractsPage } from "routes/admin/contracts";
import { FeatureFlagsPage } from "routes/admin/featureFlags";
import { SkillMastersPage } from "routes/admin/masters/skills";
import { PlansPage } from "routes/admin/plans";
import { TenantDetailPage, TenantsPage } from "routes/admin/tenants";
import { UsageDashboardPage } from "routes/admin/usage/UsageDashboardPage";
import { UserDetailPage, UsersPage } from "routes/admin/users";
import { canAccessAdminPages } from "utils/admin-utils";
import { isTenantAdmin } from "utils/authority-utils";

type RedirectWithIdProps = {
  // 旧パラメータから新パスを構築する関数。ID は Phase 1A の ID 保持戦略により
  // 旧 distribution_* と新 company_* で同一なのでそのまま流用可能。
  to: (id: string) => string;
};

/**
 * 旧 /admin/distribution/* のうち :id 付きルートを新パスへ転送する小コンポーネント。
 * @param {RedirectWithIdProps} props プロパティ
 * @return {JSX.Element} Navigate
 */
const RedirectWithId: FC<RedirectWithIdProps> = ({ to }) => {
  const { id } = useParams();
  return <Navigate to={id ? to(id) : "/companies"} replace />;
};

/**
 * 管理者権限を持つユーザーのみアクセス可能なルートを保護し、
 * 管理者向けのルーティングも担当するコンポーネント
 *
 * - SystemAdmin (isSystemAdmin): 全ルートにアクセス可能
 * - TenantAdmin (owner/admin): 配信グループ・連絡先管理のみアクセス可能
 * @return {JSX.Element} 管理者向けルート保護・ルーティングコンポーネント
 */
export const AdminRoute: FC = () => {
  const { currentUser, isLoadingUser } = useContext(AuthContext);

  // ローディング中またはcurrentUserが取得できていない場合は待機
  if (isLoadingUser || !currentUser) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">管理者権限を確認中...</div>
      </div>
    );
  }

  const isSystemAdmin = canAccessAdminPages(currentUser);
  const isTenantAdminUser = isTenantAdmin(currentUser);

  // SystemAdminでもTenantAdminでもない場合はリダイレクト
  if (!isSystemAdmin && !isTenantAdminUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Routes>
      {/* SystemAdmin専用ルート */}
      {isSystemAdmin && (
        <Route path="/" element={<AdminDashboardPageContainer />} />
      )}
      {isSystemAdmin && <Route path="/tenants" element={<TenantsPage />} />}
      {isSystemAdmin && (
        <Route path="/tenants/:id" element={<TenantDetailPage />} />
      )}
      {isSystemAdmin && <Route path="/users" element={<UsersPage />} />}
      {isSystemAdmin && (
        <Route path="/users/:id" element={<UserDetailPage />} />
      )}
      {isSystemAdmin && (
        <Route path="/feature-flags" element={<FeatureFlagsPage />} />
      )}
      {isSystemAdmin && (
        <Route path="/masters/skills" element={<SkillMastersPage />} />
      )}
      {isSystemAdmin && (
        <Route path="/usage" element={<UsageDashboardPage />} />
      )}
      {isSystemAdmin && <Route path="/contracts" element={<ContractsPage />} />}
      {isSystemAdmin && <Route path="/plans" element={<PlansPage />} />}
      {/* 旧 distribution パスを新 companies/company-groups パスへリダイレクト (ISSUE-803) */}
      <Route
        path="/distribution/groups"
        element={<Navigate to="/company-groups" replace />}
      />
      <Route
        path="/distribution/groups/new"
        element={<Navigate to="/company-groups" replace />}
      />
      <Route
        path="/distribution/groups/:id"
        element={<RedirectWithId to={id => `/company-groups/${id}`} />}
      />
      <Route
        path="/distribution/groups/:id/edit"
        element={<RedirectWithId to={id => `/company-groups/${id}`} />}
      />
      <Route
        path="/distribution/groups/:id/add-contacts"
        element={<RedirectWithId to={id => `/company-groups/${id}`} />}
      />
      <Route
        path="/distribution/contacts"
        element={<Navigate to="/companies" replace />}
      />
      <Route
        path="/distribution/contacts/new"
        element={<Navigate to="/companies" replace />}
      />
      <Route
        path="/distribution/contacts/:id"
        element={<RedirectWithId to={id => `/companies/${id}`} />}
      />
      <Route
        path="/distribution/contacts/:id/edit"
        element={<RedirectWithId to={id => `/companies/${id}`} />}
      />
      {/* 未定義パスへのフォールバック */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default AdminRoute;
